import type { Node } from '@atlaskit/editor-prosemirror/model';

const hasInlineComment = (node: Node) =>
	node.marks.some(
		(mark) => mark.type.name === 'annotation' && mark.attrs.annotationType === 'inlineComment',
	);

// In-page `#anchor` links scroll to the element the anchor macro renders.
const isAnchor = (node: Node) =>
	(node.type.name === 'extension' || node.type.name === 'inlineExtension') &&
	node.attrs.extensionKey === 'anchor';

// Real components assign document-wide heading IDs, draw the comment highlights navigation finds,
// give collapsed expands their find support, and render anchor targets, so rows with any of these
// keep their columns.
const needsColumns = (row: Node): boolean => {
	let found = false;
	row.descendants((node) => {
		found ||=
			node.type.name === 'heading' ||
			node.type.name === 'expand' ||
			node.type.name === 'nestedExpand' ||
			hasInlineComment(node) ||
			isAnchor(node);
		return !found;
	});
	return found;
};

export const holdsNestedContent = (node: Node): boolean =>
	Boolean((node.attrs as { parameters?: { nestedContent?: unknown } }).parameters?.nestedContent);

const columnRowsCache = new WeakMap<Node, ReadonlySet<Node>>();

/**
 * Rows that keep their columns. Rows linked by rowspans form one group, because a merged row cannot
 * hold another row's spanning cell: if any row in a group needs columns, the whole group keeps them.
 */
export function getColumnRows(table: Node): ReadonlySet<Node> {
	const cached = columnRowsCache.get(table);
	if (cached) {
		return cached;
	}
	const columnRows = new Set<Node>();
	let group: Node[] = [];
	let groupNeedsColumns = false;
	let coveredThrough = -1;
	const endGroup = () => {
		if (groupNeedsColumns) {
			group.forEach((row) => columnRows.add(row));
		}
		group = [];
		groupNeedsColumns = false;
	};
	table.forEach((row, _offset, index) => {
		if (index > coveredThrough) {
			endGroup();
		}
		group.push(row);
		groupNeedsColumns ||= needsColumns(row);
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Visits each cell once; work is linear in the number of cells.
		row.forEach((cell) => {
			coveredThrough = Math.max(coveredThrough, index + (cell.attrs.rowspan || 1) - 1);
		});
	});
	endGroup();
	columnRowsCache.set(table, columnRows);
	return columnRows;
}

export function getColumnCount(table: Node): number {
	let columns = 0;
	table.forEach((row) => {
		let rowColumns = 0;
		// eslint-disable-next-line @atlassian/perf-linting/no-quadratic-complexity -- Visits each cell once; work is linear in the number of cells.
		row.forEach((cell) => {
			rowColumns += cell.attrs.colspan || 1;
		});
		columns = Math.max(columns, rowColumns);
	});
	return columns;
}
